// THE WORDS A CONSENT WAS GIVEN AGAINST (founder 2026-10-15). Every consent or
// acceptance the apps and the website send carries one of these, so the record
// says which language was on screen, which message keys, and the exact words:
//
//   consentText: {
//     v: 1,
//     locale: 'pl',              the language in use (en, pl, ro, pt, es)
//     deviceLocale: 'pl-PL',     the phone's or browser's own locale, when known
//     surface: 'WORKER_APP',     WORKER_APP | BUSINESS_APP | WEBSITE | DASHBOARD
//     keys:  ['worker/components/onboarding/SectionIntro:keepMe.text', ...],
//     texts: ['Bądź na bieżąco…', ...],   each key's words exactly as rendered
//     sha256: '<hex>',           of texts.join('\n'); the server recomputes it
//   }
//
// The server (servers/onboarding/src/lib/consentRecords.js) checks the language is
// one of ours, every key is one it knows for that consent, the words fit the size
// cap, recomputes the hash itself, and stores the words beside the ENGLISH source
// of the same keys and its hash. A key is "<namespace>:<message key>": the
// namespace is the defineMessages one (apps), 'marketing' for the website, and
// 'server' for words the server itself serves (the DBS consent, the authority
// declaration), which stay English.
//
// ⚠️ A new consent, or new keys on one, also go in scripts/consent-texts/keys.mjs
// and `node scripts/consent-texts/build.mjs` in the same PR, or the server refuses
// the keys it has never heard of.
import { DEFAULT_LANG, LANG_CODES, getDeviceLocales, getLanguage, makeT } from './i18n/index.js'
import { sha256Hex } from './sha256.js'

export const CONSENT_TEXT_VERSION = 1
// The words are joined with this to make the snapshot that is hashed and stored.
export const CONSENT_TEXT_JOIN = '\n'

// What a reader sees of a rich message: the words, without the <b>/<link> markers
// t.rich turns into styling. The server strips its catalogue the same way.
export const plainText = (s) => String(s ?? '').replace(/<\/?\w+\s*\/?>/g, '')

// From parts already rendered: [{ key, text }].
export function consentTextPayload(parts, { lang, deviceLocale, surface } = {}) {
  const clean = (parts || []).filter((p) => p && p.key && typeof p.text === 'string')
  const texts = clean.map((p) => p.text)
  const locale = LANG_CODES.includes(lang) ? lang : (LANG_CODES.includes(getLanguage()) ? getLanguage() : DEFAULT_LANG)
  let device = deviceLocale
  if (device === undefined) device = getDeviceLocales()[0] || null
  return {
    v: CONSENT_TEXT_VERSION,
    locale,
    deviceLocale: device ? String(device).slice(0, 35) : null,
    ...(surface ? { surface } : {}),
    keys: clean.map((p) => p.key),
    texts,
    sha256: sha256Hex(texts.join(CONSENT_TEXT_JOIN)),
  }
}

// From an app's message namespace (defineMessages): each key rendered in the
// language in use right now, exactly as the screen's t() renders it.
//   keys: ['keepMe.text', 'keepMe.hint'] or [['confirm.outcome', { name }], ...]
//   extra: parts not in this namespace ({ key: 'server:…', text }), appended in order
//   before: parts put in front of this namespace's
export function consentTextFrom(M, keys, { lang, surface, extra = [], before = [], deviceLocale } = {}) {
  const use = LANG_CODES.includes(lang) ? lang : getLanguage()
  const t = makeT(M, use)
  const parts = (keys || []).map((k) => {
    const [key, vars] = Array.isArray(k) ? k : [k, undefined]
    return { key: `${M.ns}:${key}`, text: plainText(t(key, vars)) }
  })
  return consentTextPayload([...before, ...parts, ...extra], { lang: use, surface, deviceLocale })
}

// Two payloads as one (a consent whose words come from two screens' namespaces).
export function joinConsentTexts(a, b) {
  if (!a) return b || null
  if (!b) return a
  const parts = [...a.keys.map((key, i) => ({ key, text: a.texts[i] })), ...b.keys.map((key, i) => ({ key, text: b.texts[i] }))]
  return consentTextPayload(parts, { lang: a.locale, deviceLocale: a.deviceLocale, surface: a.surface || b.surface })
}

export default { CONSENT_TEXT_VERSION, CONSENT_TEXT_JOIN, plainText, consentTextPayload, consentTextFrom, joinConsentTexts }
